import { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Alert, Box, Chip, IconButton, MenuItem, Stack, TextField, Tooltip } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/CancelOutlined';
import FactCheckIcon from '@mui/icons-material/FactCheckOutlined';
import { approveLead, listLeads, rejectLead } from '../../api/leads';
import { useBranchFilter } from '../../context/branchContext';
import { useCounselorOptions } from '../../hooks/useCounselorOptions';
import { ApprovalStatus, LeadListItem } from '../../types/lead';
import { PageHeader } from '../../components/PageHeader';
import { TableToolbar } from '../../components/table/TableToolbar';
import { RejectLeadDialog } from './RejectLeadDialog';
import { leadDetailState } from '../../utils/leadNavigation';

const APPROVAL_LABELS: Record<ApprovalStatus, string> = {
  PENDING_APPROVAL: 'Pending Approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

/**
 * Lead Manager's dedicated "Lead Approvals" view (section 12) — reuses
 * the same GET /leads listing every other lead screen calls, filtered by
 * approvalStatus, rather than a parallel approvals data source.
 */
export const LeadApprovalsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { selectedBranchId } = useBranchFilter();
  const { counselors } = useCounselorOptions(true);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 20 });
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatus | ''>('PENDING_APPROVAL');
  const [assignedToId, setAssignedToId] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [rejectTargetId, setRejectTargetId] = useState<number | null>(null);

  const filters = {
    approvalStatus: approvalStatus || undefined,
    assignedToId: assignedToId || undefined,
    branchId: selectedBranchId,
  };

  const leadsQuery = useQuery({
    queryKey: ['leads', 'approvals', paginationModel.page, paginationModel.pageSize, filters],
    queryFn: () => listLeads({ page: paginationModel.page + 1, pageSize: paginationModel.pageSize, ...filters }),
    placeholderData: (prev) => prev,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['leads', 'approvals'] });

  const approveMutation = useMutation({
    mutationFn: (leadId: number) => approveLead(leadId),
    onSuccess: invalidate,
    onError: (err) =>
      setError(isAxiosError(err) ? err.response?.data?.message ?? 'Something went wrong' : 'Something went wrong'),
  });

  const columns: GridColDef<LeadListItem>[] = useMemo(
    () => [
      { field: 'leadNumber', headerName: 'Lead #', width: 110 },
      { field: 'name', headerName: 'Name', flex: 1, valueGetter: (_v, row) => `${row.firstName} ${row.lastName}` },
      { field: 'phone', headerName: 'Phone', width: 140 },
      {
        field: 'assignedTo',
        headerName: 'Counselor',
        width: 160,
        valueGetter: (_v, row) => (row.assignedTo ? `${row.assignedTo.firstName} ${row.assignedTo.lastName}` : '—'),
      },
      { field: 'branch', headerName: 'Branch', width: 130, valueGetter: (_v, row) => row.branch?.name ?? '—' },
      { field: 'createdAt', headerName: 'Created', width: 170, valueGetter: (_v, row) => new Date(row.createdAt).toLocaleString() },
      {
        field: 'approvalStatus',
        headerName: 'Approval',
        width: 150,
        renderCell: (params) => (
          <Chip
            size="small"
            label={APPROVAL_LABELS[params.row.approvalStatus]}
            color={
              params.row.approvalStatus === 'APPROVED'
                ? 'success'
                : params.row.approvalStatus === 'REJECTED'
                  ? 'error'
                  : 'warning'
            }
          />
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        renderCell: (params) =>
          params.row.approvalStatus === 'PENDING_APPROVAL' ? (
            <Stack direction="row">
              <Tooltip title="Approve">
                <IconButton
                  size="small"
                  color="success"
                  onClick={(e) => {
                    e.stopPropagation();
                    approveMutation.mutate(params.row.id);
                  }}
                >
                  <CheckCircleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reject">
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRejectTargetId(params.row.id);
                  }}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : null,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={FactCheckIcon}
        color="#B5451B"
        title="Lead Approvals"
        subtitle="Review Counselor-created leads pending approval."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="Approval status"
          sx={{ width: 180 }}
          value={approvalStatus}
          onChange={(e) => setApprovalStatus(e.target.value as ApprovalStatus | '')}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          <MenuItem value="PENDING_APPROVAL">Pending Approval</MenuItem>
          <MenuItem value="APPROVED">Approved</MenuItem>
          <MenuItem value="REJECTED">Rejected</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Counselor"
          sx={{ width: 180 }}
          value={assignedToId}
          onChange={(e) => setAssignedToId(e.target.value ? Number(e.target.value) : '')}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          {counselors.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.firstName} {c.lastName}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1 }}>
        <DataGrid
          rows={leadsQuery.data?.data ?? []}
          columns={columns}
          rowCount={leadsQuery.data?.meta.total ?? 0}
          loading={leadsQuery.isFetching}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          slots={{ toolbar: TableToolbar }}
          onRowClick={(params) => navigate(`/leads/${params.row.id}`, { state: leadDetailState(location) })}
        />
      </Box>

      <RejectLeadDialog
        open={rejectTargetId !== null}
        errorMessage={null}
        onClose={() => setRejectTargetId(null)}
        onSubmit={async (reason) => {
          if (!rejectTargetId) return;
          await rejectLead(rejectTargetId, reason);
          setRejectTargetId(null);
          invalidate();
        }}
      />
    </Box>
  );
};
